import { AUTH_CONSTANTS } from "@/constants";
import { isAllowedApiOrigin } from "@/config/apiOrigin";

/**
 * Partner resolution configuration from backend
 */
export interface PartnerAuthConfig {
  mode: "shared" | "dedicated";
  authority: string;
  clientId: string;
  scope: string;
  domain?: string;
  customDomain?: string;
  apiBaseUrl?: string;
}

export interface PartnerResolveResponse {
  partnerId: string;
  partnerName: string;
  slug: string;
  apiBaseUrl?: string; // Partner-specific API URL
  webBaseUrl?: string; // Partner-specific frontend URL
  authConfig: PartnerAuthConfig;
  /**
   * True for Aegle-internal, write-blocked partners (AEGL/ACAP) per ADR-035.
   * Lets the UI proactively disable write affordances; the server-side write
   * guard (400 PARTNER_READ_ONLY) remains the enforcement of record. Optional
   * until the resolve endpoint surfaces the partner `read_only` column.
   */
  readOnly?: boolean;
}

/**
 * Defense-in-depth: strip any `apiBaseUrl` (top-level or in `authConfig`) that
 * fails the origin allowlist before it is persisted to sessionStorage (#5097).
 *
 * NOTE: the primary control is read-time validation at every token-bearing
 * consumer (`getPartnerAwareApiUrl`, the auth provider) — an XSS attacker writes
 * sessionStorage *directly* and never calls this function, so write-time
 * sanitizing alone is NOT the security boundary. It hardens against a
 * compromised/misbehaving backend returning a disallowed host on the trusted
 * resolve path. Mutates in place so the cached value and the object returned to
 * callers (e.g. the auth-provider factory) stay consistent.
 */
const sanitizeApiOrigins = (config: PartnerResolveResponse): PartnerResolveResponse => {
  if (config.apiBaseUrl && !isAllowedApiOrigin(config.apiBaseUrl)) {
    console.warn(`[PartnerResolution] Rejected apiBaseUrl failing origin allowlist: ${config.apiBaseUrl}`);
    config.apiBaseUrl = undefined;
  }
  if (config.authConfig?.apiBaseUrl && !isAllowedApiOrigin(config.authConfig.apiBaseUrl)) {
    console.warn(
      `[PartnerResolution] Rejected authConfig.apiBaseUrl failing origin allowlist: ${config.authConfig.apiBaseUrl}`
    );
    config.authConfig.apiBaseUrl = undefined;
  }
  return config;
};

/**
 * Caches partner config in session storage for quick access
 */
const cachePartnerConfig = (config: PartnerResolveResponse): void => {
  try {
    sessionStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_CONFIG, JSON.stringify(sanitizeApiOrigins(config)));
  } catch (error) {
    console.warn("[PartnerResolution] Failed to cache partner config:", error);
  }
};

/**
 * Gets cached partner config from session storage
 */
export const getCachedPartnerConfig = (): PartnerResolveResponse | null => {
  try {
    const cached = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_CONFIG);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn("[PartnerResolution] Failed to read cached partner config:", error);
  }
  return null;
};

/**
 * Resolves partner configuration by email, slug, or domain
 * This endpoint is unauthenticated and called before MSAL initialization
 */
export const resolvePartner = async (params: {
  email?: string;
  slug?: string;
  domain?: string;
}): Promise<PartnerResolveResponse> => {
  const apiUrl = import.meta.env.VITE_API_SERVER_URL || "http://localhost:9999";

  // Check resolution mode from environment (default to slug)
  const resolutionMode = import.meta.env.VITE_PARTNER_RESOLUTION_MODE || "slug";
  const useSlug = resolutionMode === "slug";

  let resolveSlug = params.slug;
  let resolveDomain = params.domain;

  if (!resolveSlug && !resolveDomain) {
    const hostname = window.location.hostname;

    // Handle subdomains of localhost (e.g., test.com.localhost, healthcaresolutions.localhost)
    if (hostname.endsWith(".localhost")) {
      // Extract everything before .localhost
      const subdomain = hostname.substring(0, hostname.lastIndexOf(".localhost"));
      if (useSlug) {
        // For slug mode, just use the subdomain
        resolveSlug = subdomain;
      } else {
        // For domain mode, use full subdomain
        resolveDomain = subdomain;
      }
    } else if (hostname.endsWith(".127.0.0.1.sslip.io")) {
      // Handle sslip.io domains (e.g., dedicated.127.0.0.1.sslip.io)
      const subdomain = hostname.substring(0, hostname.indexOf(".127.0.0.1.sslip.io"));
      if (useSlug) {
        // Extract just the partner slug (e.g., "dedicated" from "dedicated.127.0.0.1.sslip.io")
        resolveSlug = subdomain;
      } else {
        // Use full hostname as domain
        resolveDomain = hostname;
      }
    } else if (hostname === "localhost") {
      // Plain localhost
      if (useSlug) {
        resolveSlug = "localhost";
      } else {
        resolveDomain = "localhost";
      }
    } else {
      // For other domains (e.g., tenant1-platform-uat.coronisops.com, platform-uat.coronisops.com)
      if (useSlug) {
        const firstSegment = hostname.split(".")[0];
        // Dedicated partner: {slug}-platform-{env} → extract the partner slug
        // e.g., "tenant1-platform-uat" → "tenant1"
        const dedicatedMatch = firstSegment.match(/^(.+)-platform-(?:dev|uat|stage|prod)$/);
        if (dedicatedMatch) {
          resolveSlug = dedicatedMatch[1];
        } else if (!/^platform-(?:dev|uat|stage|prod)$/.test(firstSegment)) {
          // Not a shared platform URL — use the segment as slug (custom domain)
          resolveSlug = firstSegment;
        }
        // Shared platform URLs (platform-uat, platform-dev, etc.) leave
        // resolveSlug undefined → falls through to environment fallback below
      } else {
        // Use full hostname as domain
        resolveDomain = hostname;
      }
    }
  }

  // If no identifier could be determined, skip the API call and use env fallback.
  // This handles the shared platform URL (e.g., platform-uat.coronisops.com) where
  // there is no partner slug to resolve.
  if (!resolveSlug && !resolveDomain && !params.email) {
    console.log("[PartnerResolution] No partner identifier found, using environment fallback");
    const fallback = getEnvironmentFallback();
    cachePartnerConfig(fallback);
    return fallback;
  }

  const queryParams = new URLSearchParams();
  if (params.email) queryParams.set("email", params.email);
  if (useSlug && resolveSlug) {
    queryParams.set("slug", resolveSlug);
  } else if (!useSlug && resolveDomain) {
    queryParams.set("domain", resolveDomain);
  }

  const url = `${apiUrl}/partners/resolve?${queryParams}`;

  console.log("[PartnerResolution] Resolving partner:", { url, params });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      // If partner resolution fails, fall back to environment config
      if (response.status === 404 || response.status === 500) {
        console.warn("[PartnerResolution] Partner not found or server error, using environment fallback");
        return getEnvironmentFallback();
      }
      throw new Error(`Partner resolution failed: ${response.status}`);
    }

    const data: PartnerResolveResponse = await response.json();

    // Cache the successful response
    cachePartnerConfig(data);

    console.log("[PartnerResolution] Partner resolved:", data);
    return data;
  } catch (error) {
    console.error("[PartnerResolution] Error resolving partner:", error);
    // Fall back to environment configuration
    return getEnvironmentFallback();
  }
};

/**
 * Provides fallback configuration from environment variables
 * Used when partner resolution endpoint is unavailable or returns an error
 */
const getEnvironmentFallback = (): PartnerResolveResponse => {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
  const authority = import.meta.env.VITE_MSAL_AUTHORITY;
  const scopes = import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES || "";

  if (!clientId || !authority) {
    throw new Error("MSAL configuration not found in environment variables");
  }

  console.log("[PartnerResolution] Using environment fallback configuration");

  return {
    partnerId: "default",
    partnerName: "Default Partner",
    slug: "default",
    authConfig: {
      mode: "shared",
      authority,
      clientId,
      scope: scopes,
      apiBaseUrl: import.meta.env.VITE_API_SERVER_URL,
    },
  };
};

/**
 * Extracts authentication scopes as an array from the scope string
 */
export const extractScopes = (scopeString: string): string[] => {
  if (!scopeString) return ["User.Read"];
  return scopeString.split(/[,\s]+/).filter(Boolean);
};
